import FileUpload from '@/components/FormFields/FileUpload/FileUpload.tsx';
import SettingTextArea from '@/components/settings/SettingTextArea.tsx';
import SettingsChipsField from '@/components/settings/SettingsChipsField.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { SEOConfig } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/v2/Button';
import {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useState,
} from 'react';

export const SearchAndSocial = () => {
  const [edit, setEdit] = useState<boolean>(false);
  const { accountConfig, updateAccountConfig } = useContext(DashboardContext);
  const [seo, setSeo] = useState<Partial<SEOConfig>>({});

  useEffect(() => {
    if (!accountConfig?.seo) return;
    setSeo(accountConfig?.seo ?? {});
  }, [accountConfig]);

  const onSave = () => {
    setEdit(false);
    updateAccountConfig({
      seo: seo,
    });
  };
  const onDiscard = () => {
    setEdit(false);
    setSeo(accountConfig?.seo ?? {});
  };

  useEffect(() => {
    if (!accountConfig) return;
    const accountConfigSeo = accountConfig?.seo;
    setEdit(JSON.stringify(accountConfigSeo) !== JSON.stringify(seo));
  }, [accountConfig, seo]);

  return (
    <div
      className={
        'relative grid h-full w-full max-w-full grid-rows-[1fr_auto] overflow-hidden'
      }
    >
      <div className={'h-full overflow-y-auto'}>
        <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5'}>
          <SettingTitle
            title="SEO"
            description="Edit global SEO to all of your landing pages."
          />

          <dl>
            <SeoForm seo={seo} onChange={setSeo} />
          </dl>
        </div>
      </div>
      {edit && (
        <div
          className={
            'mx-auto flex w-full max-w-161 justify-end gap-3 bg-white px-2.5 pt-2'
          }
        >
          <Button variant={'outline'} onClick={onDiscard}>
            Discard
          </Button>
          <Button onClick={onSave}>Save</Button>
        </div>
      )}
    </div>
  );
};

interface BaseProps {
  seo: SEOConfig;
  onChange: Dispatch<SetStateAction<Partial<SEOConfig>>>;
}

interface NormalProps extends BaseProps {
  showReset?: false;
}

interface ShowResetProps extends BaseProps {
  showReset:
    | true
    | {
        account?: boolean;
        dynamicPage?: boolean;
      };
  onReset: (type: 'account' | 'dynamicPage') => void;
}

type Props = NormalProps | ShowResetProps;

export const SeoForm = ({ seo, onChange, ...props }: Props) => {
  const [showResetModal, setShowResetModal] = useState<{
    type: 'account' | 'dynamicPage';
  }>();

  return (
    <div className="flex flex-col gap-5">
      <AlertModal
        title={`Reset to ${showResetModal?.type === 'account' ? 'Account' : 'Dynamic Page'} Defaults`}
        open={!!showResetModal}
        onClose={() => setShowResetModal(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setShowResetModal(undefined),
          },
          {
            type: 'primary',
            color: 'negative',
            title: 'Reset',
            onClick: () => {
              if ('onReset' in props) {
                const reset =
                  showResetModal?.type === 'account'
                    ? 'account'
                    : 'dynamicPage';
                props?.onReset?.(reset);
                setShowResetModal(undefined);
              }
            },
          },
        ]}
        type={'warn'}
      />
      <SettingTextArea
        title="Title"
        value={seo.title || ''}
        setValue={(v: string) => onChange({ ...seo, title: v })}
        rows={1}
      />
      <SettingTextArea
        title="Description"
        value={seo.description || ''}
        setValue={(v: string) => onChange({ ...seo, description: v })}
      />
      <SettingsChipsField
        title="Keywords"
        selected={seo.keywords?.map((k) => ({ id: k, title: k })) || []}
        setSelected={(selected) =>
          onChange({ ...seo, keywords: selected.map((s) => s.title) })
        }
        options={[]}
      />
      <div className={'space-y-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">Image</h2>
        <FileUpload
          image={seo.image}
          onSelected={(image) => onChange({ ...seo, image })}
        />
      </div>
      <div className={'space-y-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Favicon
        </h2>
        <FileUpload
          image={seo.favicon}
          onSelected={(favicon) => onChange({ ...seo, favicon })}
        />
      </div>
    </div>
  );
};
